import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { currentLocale } from '../components/numericFormat';
import { EMPTY } from './perspectiveValues';
import { PerspectiveScope, PhaseChecklistRow } from './sitePerspectiveApi';
import { usePhaseChecklists } from './usePhaseChecklists';

/**
 * "Offene Aufgaben, Verantwortliche und Fristen" — as a table, not as a text field.
 *
 * The open tasks of a phase already exist: they are the checklists its activation created, their
 * owner is the role each is assigned to, and their deadline is the checklist's due date. Asking
 * someone to type all that into the perspective would produce a second list that is wrong the
 * moment a box is ticked, so the point reads the checklists instead.
 *
 * One line per task. Two open items on one checklist are two lines with the same owner and the same
 * deadline — the question is about tasks, and a reader counting open work should not have to open a
 * checklist to find out how much is in it.
 *
 * A task links into the checklist itself, where it is worked on. Reading here, working there: this
 * table is the status of the phase, not a second place to tick things off.
 */

interface Properties {
    /** Absent while no property is selected; then there is no phase to read. */
    scope: PerspectiveScope | null;
}

/** Where the checklist is worked on — the running checklist in the process panel. */
const checklistLink = (row: PhaseChecklistRow): string =>
    `/settings?panel=processManagement&tab=tasks&checklistInstanceId=${encodeURIComponent(row.checklistId)}`;

const dueLabel = (row: PhaseChecklistRow): string => {
    if (!row.due) return EMPTY;

    const parsed = new Date(row.due);
    return Number.isNaN(parsed.getTime()) ? EMPTY : parsed.toLocaleDateString(currentLocale());
};

export default function PhaseChecklists({ scope }: Properties) {
    const { t } = useTranslation();
    const { rows, loading, error } = usePhaseChecklists(scope);

    if (loading) {
        return (
            <span role="status" className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                {t('global.loading')} …
            </span>
        );
    }

    if (error) {
        return <span className="text-sm text-destructive">{error}</span>;
    }

    if (rows.length === 0) {
        return <span className="text-sm text-muted-foreground">{t('portfolioDashboard.checklists.none')}</span>;
    }

    return (
        <div className="overflow-x-auto">
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{t('portfolioDashboard.checklists.task')}</TableHead>
                        <TableHead>{t('portfolioDashboard.checklists.responsible')}</TableHead>
                        <TableHead>{t('portfolioDashboard.checklists.due')}</TableHead>
                        <TableHead>{t('portfolioDashboard.checklists.state')}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row, index) => (
                        <TableRow
                            key={`${row.checklistId}-${index}`}
                            className={cn(row.done && 'text-muted-foreground line-through')}
                        >
                            <TableCell>
                                <Link
                                    to={checklistLink(row)}
                                    className="underline underline-offset-2 hover:no-underline"
                                    title={t('portfolioDashboard.checklists.open_checklist', {
                                        checklist: row.checklist,
                                    })}
                                >
                                    {row.task}
                                </Link>
                            </TableCell>
                            <TableCell>{row.role ?? EMPTY}</TableCell>
                            <TableCell>{dueLabel(row)}</TableCell>
                            <TableCell>
                                {row.done
                                    ? t('portfolioDashboard.checklists.done')
                                    : t('portfolioDashboard.checklists.open')}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
